/**
 * The app top bar: brand, the cookbook context-switcher, and an avatar menu
 * holding the caller's identity + the log-out action.
 *
 * Security note: identity values (name / email) are display-only; the raw
 * Zitadel subject id is never rendered (only used internally, e.g. to scope
 * per-user local state). The access token is NEVER rendered here (it lives
 * only in the oidc-client store and the Authorization header).
 */

import type { MeResponse } from "@tjommi/shared";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { fetchPendingUsers } from "../api";
import { useCookbook } from "../cookbook/CookbookContext";
import { getConfiguredLinkProviders } from "../identity/providers";
import { useClickAway } from "../lib/useClickAway";
import { IdpLinkControl } from "./IdpLinkControl";

/** Neutral placeholder glyph shown when neither a name nor an email is available. */
const PLACEHOLDER_INITIALS = "?";

/**
 * Derive up-to-2-letter initials from the best available identity field.
 * Falls back through displayName -> email -> a neutral glyph; the subject id
 * is never used for display.
 */
function initials(me: MeResponse): string {
  const source = me.displayName?.trim() || me.email?.trim();
  if (!source) return PLACEHOLDER_INITIALS;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const letters = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  return (letters || source.slice(0, 2)).toUpperCase();
}

/** Close a popover when the user clicks/taps outside the given element. */
export function TopBar({
  me,
  accessToken,
  onLogout,
  onSessionExpired,
}: {
  me: MeResponse;
  accessToken: string;
  onLogout: () => void;
  /** Triggers a normal re-login (e.g. a token predating the Zitadel-API
   *  audience scope, or an expired session) — reused by IdpLinkControl. */
  onSessionExpired: () => void;
}): React.JSX.Element {
  const { cookbooks, active, setActiveId } = useCookbook();
  // One "Koble til <provider>" affordance per provider this deploy has
  // configured (empty idpId ⇒ that provider is simply absent from the list).
  const linkProviders = getConfiguredLinkProviders();
  // The user's OWN cookbook (each owns exactly one) — the shareable one.
  const ownedCookbook = cookbooks.find((c) => c.role === "owner");
  const navigate = useNavigate();
  const [cbOpen, setCbOpen] = useState(false);
  const [idOpen, setIdOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const cbRef = useClickAway(() => setCbOpen(false));
  const idRef = useClickAway(() => setIdOpen(false));

  // Admin-only notification badge: a best-effort count of users awaiting
  // approval. This is purely a UX cue — any failure (including an expired
  // token) just leaves the badge hidden; it must never break the top bar or
  // force a re-login (that's AdminPage's job when the admin actually acts).
  useEffect(() => {
    if (!me.isAdmin) {
      setPendingCount(0);
      return;
    }
    let ignore = false;
    fetchPendingUsers(accessToken)
      .then((users) => {
        if (!ignore) setPendingCount(users.length);
      })
      .catch(() => {
        if (!ignore) setPendingCount(0);
      });
    return () => {
      ignore = true;
    };
  }, [me.isAdmin, accessToken]);

  /**
   * Switch the active cookbook. Changing cookbooks returns to the feed (home) so
   * you don't stay on a recipe/editor that belongs to the cookbook you just left
   * (any pending editor edit is flushed on unmount). Re-picking the active one
   * just closes the menu.
   */
  function chooseCookbook(id: string): void {
    setCbOpen(false);
    if (id !== active.id) {
      setActiveId(id);
      navigate("/");
    }
  }

  return (
    <header className="topbar" data-testid="topbar">
      <Link to="/" className="brand" data-testid="brand-home">
        deSjeff
        <span className="dot" />
      </Link>

      {/* Cookbook context-switcher */}
      <div className="cb-switch" ref={cbRef}>
        <button
          type="button"
          className="cb-switch-btn"
          data-testid="cookbook-switch"
          aria-haspopup="menu"
          aria-expanded={cbOpen}
          onClick={() => setCbOpen((o) => !o)}
        >
          <span aria-hidden="true">📖</span>
          <b data-testid="active-cookbook">{active.name}</b>
          <span className="chev" aria-hidden="true">
            ▾
          </span>
        </button>
        {cbOpen && (
          <div className="cb-menu" role="menu" data-testid="cookbook-menu">
            {cookbooks.map((cb) => (
              <div key={cb.id} className="cb-menu-row">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={cb.id === active.id}
                  className={`cb-menu-item${cb.id === active.id ? " is-active" : ""}`}
                  data-testid={`cookbook-option-${cb.id}`}
                  onClick={() => chooseCookbook(cb.id)}
                >
                  <span className="cb-name">{cb.name}</span>
                  <span className="chip" data-testid={`cookbook-role-${cb.id}`}>
                    {cb.role}
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="top-spacer" />

      {/* Identity + logout */}
      <div className="avatar-wrap" ref={idRef}>
        <button
          type="button"
          className="avatar"
          data-testid="avatar-button"
          aria-haspopup="menu"
          aria-expanded={idOpen}
          aria-label="Konto"
          onClick={() => setIdOpen((o) => !o)}
        >
          {initials(me)}
          {me.isAdmin && pendingCount > 0 && (
            <span className="pending-badge" data-testid="pending-badge-avatar">
              {pendingCount}
            </span>
          )}
        </button>
        {idOpen && (
          <div className="identity-menu" role="menu" data-testid="identity-menu">
            <div className="identity-name" data-testid="display-name">
              {me.displayName ?? "—"}
            </div>
            <div className="identity-email" data-testid="email">
              {me.email ?? "—"}
            </div>
            {linkProviders.map((provider) => (
              <IdpLinkControl
                key={provider.key}
                subject={me.subject}
                accessToken={accessToken}
                provider={provider}
                onSessionExpired={onSessionExpired}
              />
            ))}
            {/* Share/manage YOUR OWN cookbook (P2-F2). Each user owns exactly one
                (auto-provisioned); the API re-checks `cookbook:manage` — this is
                a UI affordance. Placed at the top of the menu for discoverability. */}
            {ownedCookbook && (
              <Link
                to={`/cookbooks/${ownedCookbook.id}/manage`}
                className="link-btn identity-menu-link"
                data-testid="share-cookbook-link"
                onClick={() => setIdOpen(false)}
              >
                Del kokebok
              </Link>
            )}
            {me.isAdmin && (
              <Link
                to="/admin"
                className="link-btn identity-menu-link"
                data-testid="admin-link"
                onClick={() => setIdOpen(false)}
              >
                Ventende brukere
                {pendingCount > 0 && (
                  <span
                    className="pending-badge pending-badge-inline"
                    data-testid="pending-badge-menu"
                  >
                    {pendingCount}
                  </span>
                )}
              </Link>
            )}
            <Link
              to="/personvern"
              className="link-btn identity-menu-link"
              data-testid="privacy-link"
              onClick={() => setIdOpen(false)}
            >
              Personvern
            </Link>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginTop: "14px", width: "100%", justifyContent: "center" }}
              data-testid="logout-button"
              onClick={onLogout}
            >
              Logg ut
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
